import Skeleton from '@mui/material/Skeleton';
import { alpha, css, type SxProps, styled, type Theme } from '@mui/material/styles';
import MaterialTableCell from '@mui/material/TableCell';
import { DateLabel } from '@openx/components-core/lib/DateLabel/DateLabel.tsx';
import { Ellipsis } from '@openx/components-core/lib/Ellipsis/Ellipsis.tsx';
import { HighlightedPhrase } from '@openx/components-core/lib/HighlightedPhrase/HighlightedPhrase.tsx';
import { ValuePlaceholder } from '@openx/components-core/lib/Value/ValuePlaceholder/ValuePlaceholder.tsx';
import type { BaseRow, StickyPosition, TableColumn } from '@openx/types/tableHelpers.ts';
import { STICKY_POSITION } from '@openx/types/tableHelpers.ts';
import { type JSX, memo, type ReactNode, useCallback, useMemo } from 'react';

import { findHighlightPhraseForField, type HighlightRules } from '../highlightRules.ts';

import { MultiValueTableCell } from './MultiValueTableCell.tsx';

function LoadingCell(): JSX.Element {
  return (
    <div style={{ width: '75%' }}>
      <Skeleton animation="wave" />
    </div>
  );
}

export interface TableCellProps<RowT extends BaseRow> {
  column: TableColumn<RowT>;
  rowData?: RowT;
  className?: string;
  highlightRules?: HighlightRules;
  overrideTableCellStyles?: SxProps<Theme>;
  stickyLeftOffset?: number;
  stickyRightOffset?: number;
  isFirstNonCheckbox?: boolean;
  isLeadingStickyRight?: boolean;
}

const StyledMaterialTableCell = styled(MaterialTableCell, {
  shouldForwardProp: prop =>
    !['sticky', 'stickyLeftOffset', 'stickyRightOffset', 'isFirstNonCheckbox', 'isLeadingStickyRight'].includes(
      prop as string
    ),
})<{
  sticky?: StickyPosition;
  stickyLeftOffset?: number;
  stickyRightOffset?: number;
  isFirstNonCheckbox?: boolean;
  isLeadingStickyRight?: boolean;
}>`
  padding: ${({ theme }) => theme.spacing(0.5, 1.25)};

  ${({ sticky, stickyLeftOffset, theme }) =>
    sticky === STICKY_POSITION.LEFT &&
    css`
      position: sticky;
      left: ${stickyLeftOffset || 0}px;
      z-index: 2;
      background-color: ${theme.palette.background.paper};

      [data-scrollable='true'] & {
        box-shadow: 3px 0px 4px -1px ${alpha(theme.palette.common.black, 0.08)};
      }

      tr:hover & {
        background-color: ${theme.palette.background.boxDark};
      }

      tr.Mui-selected & {
        background-color: ${theme.palette.action.selected};
      }
    `}

  ${({ sticky, stickyRightOffset, isLeadingStickyRight, theme }) =>
    sticky === STICKY_POSITION.RIGHT &&
    css`
      position: sticky;
      right: ${stickyRightOffset || 0}px;
      z-index: 2;
      background-color: ${theme.palette.background.paper};

      ${
        isLeadingStickyRight
          ? css`
            [data-scrollable='true'] & {
              box-shadow: -2px 0px 4px 0px ${alpha(theme.palette.common.black, 0.08)};
            }
          `
          : ''
      }

      tr:hover & {
        background-color: ${theme.palette.background.boxDark};
      }

      tr.Mui-selected & {
        background-color: ${theme.palette.action.selected};
      }
    `}

  ${({ isFirstNonCheckbox }) => isFirstNonCheckbox && css`padding-left: 32px;`}
`;

const TableCell = <RowT extends BaseRow>(props: TableCellProps<RowT>): JSX.Element => {
  const {
    column,
    className,
    rowData,
    highlightRules,
    overrideTableCellStyles,
    stickyLeftOffset,
    stickyRightOffset,
    isFirstNonCheckbox,
    isLeadingStickyRight,
  } = props;

  const highlightPhrase = useMemo(
    () => highlightRules && findHighlightPhraseForField(highlightRules, column.key),
    [highlightRules, column.key]
  );

  const highlighter = useCallback(
    (content: string): ReactNode => {
      if (highlightPhrase) {
        return <HighlightedPhrase searchPhrase={highlightPhrase}>{content}</HighlightedPhrase>;
      }

      return content;
    },
    [highlightPhrase]
  );

  const EllipsisWrapper = useCallback(
    ({ children }) => {
      if (column.ellipsis && column.ellipsisMaxWidth) {
        return <Ellipsis width={column.ellipsisMaxWidth}>{children}</Ellipsis>;
      }

      return children || null;
    },
    [column.ellipsis, column.ellipsisMaxWidth]
  );

  const renderRowData = useCallback(
    (data: RowT): ReactNode => {
      if (column.render) {
        return column.render(data, highlighter);
      }

      if (column.multivalue) {
        return (
          data[column.key]?.length && (
            <MultiValueTableCell
              phrase={highlightPhrase}
              mapBy={column.mapBy}
              values={data[column.key]}
              highlighter={highlighter}
              showTooltip={!!column.multivalueTooltip}
            />
          )
        );
      }

      if (column.isDate) {
        if (!data[column.key]) {
          return <ValuePlaceholder />;
        }

        if (column.isExactDate) {
          return (
            <DateLabel
              date={data[column.key]}
              displayLocalTime={false}
              displayExactDate
              dateFormat="yyyy-MM-dd"
              showOffset={false}
            />
          );
        }

        if (column.isDateTime) {
          return <DateLabel date={data[column.key]} displayExactDate />;
        }

        return <DateLabel date={data[column.key]} />;
      }

      const value = column.mapBy?.[data[column.key]]?.name || data[column.key];
      return value === false || value ? <EllipsisWrapper>{highlighter(value)}</EllipsisWrapper> : <ValuePlaceholder />;
    },
    [highlighter, column, highlightPhrase, EllipsisWrapper]
  );

  return (
    <StyledMaterialTableCell
      width={column.width}
      align={column.align}
      className={className}
      variant="body"
      data-test={column.dataTest ?? 'table-cell'}
      sticky={column.sticky}
      stickyLeftOffset={stickyLeftOffset}
      stickyRightOffset={stickyRightOffset}
      isFirstNonCheckbox={isFirstNonCheckbox}
      isLeadingStickyRight={isLeadingStickyRight}
      sx={
        [
          overrideTableCellStyles,
          column.style,
          column.minWidth ? { minWidth: column.minWidth } : undefined,
          isFirstNonCheckbox ? { paddingLeft: '32px' } : undefined,
          column.width?.endsWith('%') ? { maxWidth: 0, overflow: 'hidden' } : undefined,
        ].filter(Boolean) as SxProps<Theme>
      }
    >
      {rowData ? renderRowData(rowData) || <ValuePlaceholder /> : <LoadingCell />}
    </StyledMaterialTableCell>
  );
};

export const GenericTableCell = memo(TableCell) as typeof TableCell;
